import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faEnvelope,
  faFilePdf,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { BASE_URL } from "../../config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import PlanBadge from "../PlanBadge";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const formatDateOnly = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const dateOnly = String(value).split("T")[0].split(" ")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : null;
};

const parseDateOnly = (value) => {
  const dateOnly = formatDateOnly(value);
  if (!dateOnly) return null;

  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
      <svg
        viewBox="0 0 24 24"
        className="mb-4 h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>

      <div className="text-base font-semibold text-gray-900">{title}</div>
      <div className="mt-1 text-sm text-gray-500">{subtitle}</div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => navigate("/ape-scheduler/add")}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
      >
        <PlusIcon className="h-5 w-5" />
        📝 Set Up New APE Schedule
      </motion.button>
    </div>
  );
};

const FilterSection = ({ corporates, onFilterChange, filters }) => {
  const [filterCorp, setFilterCorp] = useState(filters.corp_code);
  const [filterStatus, setFilterStatus] = useState(filters.status);
  const [filterDateFrom, setFilterDateFrom] = useState(
    parseDateOnly(filters.date_from)
  );
  const [filterDateTo, setFilterDateTo] = useState(
    parseDateOnly(filters.date_to)
  );

  // Apply filters immediately on change
  useEffect(() => {
    onFilterChange({
      corp_code: filterCorp,
      status: filterStatus,
      date_from: formatDateOnly(filterDateFrom),
      date_to: formatDateOnly(filterDateTo),
    });
    // }, [filterCorp, filterStatus, filterDateFrom, filterDateTo, onFilterChange]);
  }, [filterCorp, filterStatus, filterDateFrom, filterDateTo]);

  const handleResetFilters = () => {
    setFilterCorp("");
    setFilterStatus("ALL");
    setFilterDateFrom(null);
    setFilterDateTo(null);
    onFilterChange({
      corp_code: "",
      status: "ALL",
      date_from: null,
      date_to: null,
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Corporate
          </label>
          <select
            value={filterCorp}
            onChange={(e) => setFilterCorp(e.target.value)}
            className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Corporates</option>
            {corporates.map((corp) => (
              <option
                key={corp.value}
                value={corp.value.split("]")[0].replace("[", "")}
              >
                {corp.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Final">Final</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date From
          </label>
          <DatePicker
            selected={filterDateFrom}
            onChange={setFilterDateFrom}
            className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 p-2"
            dateFormat="yyyy-MM-dd"
            placeholderText="Select date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date To
          </label>
          <DatePicker
            selected={filterDateTo}
            onChange={setFilterDateTo}
            className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 p-2"
            dateFormat="yyyy-MM-dd"
            placeholderText="Select date"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleResetFilters}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const ApeScheduleListPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSetupId, setSelectedSetupId] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set()); // Persist selections across pages
  const [selectedStatus, setSelectedStatus] = useState(null); // Track the status of selected rows
  const [filters, setFilters] = useState({
    corp_code: "",
    status: "ALL",
    date_from: null,
    date_to: null,
  });

  const [isProcessing, setIsProcessing] = useState(false);

  // Load dropdown options
  const [corporates, setCorporates] = useState([]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Load dropdown data
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const corpRes = await axios.get(
          `${BASE_URL}/ape-scheduler/corporates`,
          { headers: getAuthHeaders() }
        );
        setCorporates(corpRes.data.data);
      } catch (error) {
        toast.error("Failed to load dropdowns.");
      }
    };
    loadDropdowns();
  }, []);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery,
        status: filters.status === "ALL" ? undefined : filters.status,
        sort_by: sortBy,
        sort_direction: sortDirection,
        corp_code: filters.corp_code || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      };

      const response = await axios.get(`${BASE_URL}/ape-scheduler/setups`, {
        headers: getAuthHeaders(),
        params,
      });
      setData(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error("Failed to fetch schedules.", { autoClose: 3000 });
      setTimeout(() => navigate("/"), 2000);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    filters,
    sortBy,
    sortDirection,
    navigate,
  ]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleRowSelect = (originalRow) => {
    if (!originalRow || !originalRow.tbl_id || !originalRow.status_flag) return;

    const rowStatus = originalRow.status_flag;

    // If no selections yet, set the status
    if (selectedRows.size === 0) {
      setSelectedStatus(rowStatus);
      const newSelected = new Set([originalRow.tbl_id]);
      setSelectedRows(newSelected);
      return;
    }

    // If trying to select a different status, prevent it
    if (selectedStatus !== rowStatus) {
      toast.warning(
        `Cannot select ${rowStatus} rows when ${selectedStatus} rows are already selected.`
      );
      return;
    }

    // Same status, toggle
    const newSelected = new Set(selectedRows);
    if (newSelected.has(originalRow.tbl_id)) {
      newSelected.delete(originalRow.tbl_id);
      if (newSelected.size === 0) {
        setSelectedStatus(null);
      }
    } else {
      newSelected.add(originalRow.tbl_id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (data.length === 0) return;

    const firstRowStatus = data[0]?.status_flag;
    if (
      !firstRowStatus ||
      (selectedStatus && selectedStatus !== firstRowStatus)
    ) {
      toast.warning(
        "Cannot select all: mixed statuses or incompatible with current selection."
      );
      return;
    }

    const newSelected = new Set(selectedRows);
    const allSameStatus = data.every(
      (row) => row.status_flag === firstRowStatus
    );
    if (!allSameStatus) {
      toast.warning("Cannot select all: mixed statuses in current page.");
      return;
    }

    if (selectedStatus === null) {
      setSelectedStatus(firstRowStatus);
    }

    data.forEach((row) => {
      if (row && row.tbl_id) {
        if (selectedRows.has(row.tbl_id)) {
          newSelected.delete(row.tbl_id);
        } else {
          newSelected.add(row.tbl_id);
        }
      }
    });

    if (newSelected.size === 0) {
      setSelectedStatus(null);
    }

    setSelectedRows(newSelected);
  };

  const handleAction = async (label, setupIds) => {
    if (isProcessing) return;
    if (!setupIds || setupIds.length === 0) {
      toast.warning("No rows selected.");
      return;
    }

    if (label === "Finalize & Generate LOA") {
      const result = await Swal.fire({
        title: "Finalize schedule and Generate LOA?",
        text: "Would you like finalize the schedule? This action will also generate APE LOA for the selected members.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Finalize & Generate LOA",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#10B981",
      });

      if (result.isConfirmed) {
        setIsProcessing(true);
        const loadingSwal = Swal.fire({
          title: "Please wait...",
          text: "While Finalizing and Generating LOA",
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        try {
          await axios.post(
            `${BASE_URL}/ape-scheduler/finalize-loa`,
            { setup_ids: setupIds },
            { headers: getAuthHeaders() }
          );
          Swal.close(); // Close loading modal
          toast.success(
            "Final Schedule has been Saved! Final Schedule can now be viewed and sent to the corporate."
          );
          fetchSchedules();
          setSelectedRows(new Set());
          setSelectedStatus(null);
        } catch (error) {
          Swal.close(); // Close loading modal
          toast.error("Failed to finalize and generate LOA.");
        } finally {
          Swal.close(); // Close loading modal
          setIsProcessing(false);
        }
      }
      return;
    }

    alert(`${label}: ${setupIds.join(", ")}`);
  };

  const getSelectedRowIds = () => Array.from(selectedRows);

  const statusMap = {
    Draft: { name: "Draft", color: "yellow" },
    Final: { name: "Final", color: "green" },
    Deleted: { name: "Deleted", color: "red" },
    default: { name: "Unknown", color: "gray" },
  };

  const handleSendEmail = async (setupId) => {
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/send-email/setup/${setupId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Email sent successfully", { autoClose: 3000 });
    } catch (error) {
      toast.error("Failed to send email.", { autoClose: 3000 });
    }
  };

  const handleDelete = async () => {
    if (!selectedSetupId) return;
    setLoading(true);
    try {
      await axios.delete(
        `${BASE_URL}/ape-scheduler/setups/${selectedSetupId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Setup deleted successfully", { autoClose: 3000 });
      fetchSchedules();
    } catch (error) {
      toast.error("Failed to delete setup.", { autoClose: 3000 });
    } finally {
      setDeleteModalOpen(false);
      setSelectedSetupId(null);
      setLoading(false);
    }
  };

  const renderActions = (row) => {
    const status = row.original.status_flag;
    const setupId = row.original.tbl_id;

    if (status === "Draft") {
      return (
        <div className="flex flex-row gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/ape-scheduler/view/${setupId}`);
            }}
            title="View"
            className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
          >
            <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/ape-scheduler/edit/${setupId}`);
            }}
            title="Edit"
            className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
          >
            <FontAwesomeIcon icon={faEdit} className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSetupId(setupId);
              setDeleteModalOpen(true);
            }}
            title="Delete"
            className="rounded-md p-1 text-rose-600 hover:bg-rose-50 hover:text-rose-800"
          >
            <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isProcessing)
                handleAction("Finalize & Generate LOA", [setupId]);
            }}
            title="Finalize & Generate LOA"
            disabled={isProcessing}
            className={clsx(
              "rounded-md p-1",
              isProcessing
                ? "text-gray-400 cursor-not-allowed"
                : "text-green-600 hover:bg-green-50 hover:text-green-800"
            )}
          >
            <FontAwesomeIcon icon={faCheck} className="h-5 w-5" />
          </button>
        </div>
      );
    } else if (status === "Final") {
      // return (
      //   <div className="flex flex-row gap-1">
      //     <button
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         navigate(`/ape-scheduler/view/${setupId}`);
      //       }}
      //       title="View"
      //       className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
      //     >
      //       <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
      //     </button>

      //     <button
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         handleAction("Generate Corporate PDF", [setupId]);
      //       }}
      //       title="Generate Corporate PDF"
      //       className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
      //     >
      //       <FontAwesomeIcon icon={faFilePdf} className="h-5 w-5" />
      //     </button>

      //     <button
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         handleAction("Generate Provider PDF", [setupId]);
      //       }}
      //       title="Generate Provider PDF"
      //       className="rounded-md p-1 text-green-600 hover:bg-green-50 hover:text-green-800"
      //     >
      //       <FontAwesomeIcon icon={faFilePdf} className="h-5 w-5" />
      //     </button>
      //     <button
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         handleAction("Send Email To Corporate", [setupId]);
      //       }}
      //       title="Send Email To Corporate"
      //       className="rounded-md p-1 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800"
      //     >
      //       <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5" />
      //     </button>
      //     <button
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         handleAction("Send Email To Providers", [setupId]);
      //       }}
      //       title="Send Email To Providers"
      //       className="rounded-md p-1 text-purple-600 hover:bg-purple-50 hover:text-purple-800"
      //     >
      //       <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5" />
      //     </button>
      //   </div>
      // );

      return (
        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/ape-scheduler/view/${setupId}`);
            }}
            title="View"
            className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
          >
            <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
          </button>

          <button
            onClick={() => handleGenerateCorporatePdf(row.original.tbl_id)}
            title="Generate Corporate PDF"
            className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800 "
          >
            <FontAwesomeIcon icon={faFilePdf} className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleGenerateProviderPdf(row.original.tbl_id)}
            title="Generate Provider PDF"
            className="rounded-md p-1 text-green-600 hover:bg-green-50 hover:text-green-800"
          >
            <FontAwesomeIcon icon={faFilePdf} className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleSendCorporateEmail(row.original.tbl_id)}
            title="Send Email To Corporate"
            className="rounded-md p-1 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800"
          >
            <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleSendProviderEmail(row.original.tbl_id)}
            title="Send Email To Providers"
            className="rounded-md p-1 text-purple-600 hover:bg-purple-50 hover:text-purple-800"
          >
            <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5" />
          </button>
        </div>
      );
    }

    return null;
  };

  // GENERATE PDF AND SEND EMAIL FEAUTERS
  // Add handlers
  const handleGenerateCorporatePdf = async (setupId) => {
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/generate-corporate-pdf/${setupId}`,
        {},
        { headers: getAuthHeaders() }
      );
      Swal.fire("Success", "Corporate PDF generation queued!", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to queue PDF", "error");
    }
  };

  const handleGenerateProviderPdf = async (setupId) => {
    // Similar for provider
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/generate-provider-pdf/${setupId}`,
        {},
        { headers: getAuthHeaders() }
      );
      Swal.fire("Success", "Provider PDF generation queued!", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to queue PDF", "error");
    }
  };

  const handleSendCorporateEmail = async (setupId) => {
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/send-email/setup/${setupId}?type=corporate`,
        {},
        { headers: getAuthHeaders() }
      );
      Swal.fire("Success", "Corporate email queued!", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to queue email", "error");
    }
  };

  const handleSendProviderEmail = async (setupId) => {
    // Similar for provider
    try {
      await axios.post(
        `${BASE_URL}/ape-scheduler/send-email/setup/${setupId}?type=provider`,
        {},
        { headers: getAuthHeaders() }
      );
      Swal.fire("Success", "Provider email queued!", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to queue email", "error");
    }
  };
  // GENERATE PDF AND SEND EMAIL FEAUTERS

  const columns = useMemo(
    () => [
      {
        id: "selection",
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <input
            type="checkbox"
            {...getToggleAllRowsSelectedProps()}
            onChange={handleSelectAll}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={isProcessing}
          />
        ),
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.has(row.original?.tbl_id)}
            onChange={() => handleRowSelect(row.original)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={isProcessing}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: "Setup ID",
        accessor: "tbl_id",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-900">{value}</span>
        ),
      },
      {
        Header: "Corp Name",
        accessor: "corp_name",
        Cell: ({ value }) => (
          <span className="font-medium text-gray-900">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Plan",
        accessor: "plan_name",
        Cell: ({ row, value }) => (
          // <span className="text-gray-700">{value || "N/A"}</span>
          <span className="text-gray-900">
            {row.original.plan_code === "All"
              ? "All"
              : <PlanBadge value={value} /> || "N/A"}
          </span>
        ),
      },
      {
        Header: "Provider",
        accessor: "provider_name",
        Cell: ({ row, value }) => (
          // <span className="text-gray-700">{value || "N/A"}</span>
          <span className="text-gray-900">
            {row.original.provider_code === "All" ? "All" : value || "N/A"}
          </span>
        ),
      },
      {
        Header: "Package",
        accessor: "package_name",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Member Type",
        accessor: "member_type",
        Cell: ({ value }) => (
          <span className="text-gray-700">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Date From",
        accessor: "date_from",
        Cell: ({ value }) => formatDateOnly(value) || "N/A",
      },
      {
        Header: "Date To",
        accessor: "date_to",
        Cell: ({ value }) => formatDateOnly(value) || "N/A",
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value }) =>
          value
            ? `₱${parseFloat(value).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}`
            : "N/A",
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ value }) => (
          <span className="text-gray-600">{value || "N/A"}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Scheduled Members",
        accessor: "member_count",
        Cell: ({ value }) => (
          <span className="font-medium text-blue-600">{value || 0}</span>
        ),
      },
      {
        Header: "Actions1",
        accessor: "actions1",
        disableSortBy: true,
        Cell: ({ row }) => renderActions(row),
      },
      // {
      //   Header: "Actions2",
      //   accessor: "actions2",
      //   Cell: ({ row }) => (
      //     <div className="flex space-x-2">
      //       <button
      //         onClick={() => handleGenerateCorporatePdf(row.original.tbl_id)}
      //         className="text-blue-600 hover:text-blue-900"
      //         title="Generate Corporate PDF"
      //       >
      //         <FontAwesomeIcon icon={faFilePdf} />
      //       </button>
      //       <button
      //         onClick={() => handleGenerateProviderPdf(row.original.tbl_id)}
      //         className="text-green-600 hover:text-green-900"
      //         title="Generate Provider PDF"
      //       >
      //         <FontAwesomeIcon icon={faFilePdf} />
      //       </button>
      //       <button
      //         onClick={() => handleSendCorporateEmail(row.original.tbl_id)}
      //         className="text-orange-600 hover:text-orange-900"
      //         title="Send to Corporate"
      //       >
      //         <FontAwesomeIcon icon={faEnvelope} />
      //       </button>
      //       <button
      //         onClick={() => handleSendProviderEmail(row.original.tbl_id)}
      //         className="text-purple-600 hover:text-purple-900"
      //         title="Send to Provider"
      //       >
      //         <FontAwesomeIcon icon={faEnvelope} />
      //       </button>
      //     </div>
      //   ),
      // },
    ],
    [selectedRows, data, navigate, selectedStatus]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / itemsPerPage),
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    setPageSize(itemsPerPage);
    gotoPage(currentPage - 1);
  }, [itemsPerPage, currentPage, setPageSize, gotoPage]);

  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const maxPagesToShow = 5;
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" />
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <CubeIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                APE Scheduler
              </h1>
              <p className="text-sm text-gray-500">
                Manage and finalize (APE) Annual Physical Exam Schedule
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/ape-scheduler/add")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
          >
            <PlusIcon className="h-5 w-5" />
            📝 Set Up New APE Schedule
          </motion.button>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search schedules..."
            className="w-full rounded-md border border-gray-200 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            disabled={isProcessing}
          />
        </div>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-32"
          disabled={isProcessing}
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      </div>

      {/* Filters Section */}
      <FilterSection
        corporates={corporates}
        onFilterChange={handleFilterChange}
        filters={filters}
      />

      {/* Selected Actions Buttons */}
      {selectedRows.size > 0 && selectedStatus && (
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <div className="flex flex-col sm:flex-row gap-2 justify-between items-center">
            <span className="text-sm text-blue-800">
              {selectedRows.size} {selectedStatus} rows selected
            </span>
            <div className="flex gap-2">
              {isProcessing ? (
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  Processing...
                </div>
              ) : selectedStatus === "Draft" ? (
                <button
                  onClick={() =>
                    handleAction("Finalize & Generate LOA", getSelectedRowIds())
                  }
                  disabled={isProcessing}
                  className="px-3 py-1 bg-green-600 text-white text-lg rounded hover:bg-green-700 active:scale-95 active:shadow-inner transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="animate-pulse">
                    <FontAwesomeIcon icon={faCheck} className="h-5 w-5" />{" "}
                    Finalize & Generate LOA
                  </span>
                </button>
              ) : selectedStatus === "Final" ? (
                <>
                  <button
                    onClick={() =>
                      handleAction(
                        "Generate Corporate PDF",
                        getSelectedRowIds()
                      )
                    }
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    Generate Corporate PDF
                  </button>
                  <button
                    onClick={() =>
                      handleAction("Generate Provider PDF", getSelectedRowIds())
                    }
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    Generate Provider PDF
                  </button>
                  <button
                    onClick={() =>
                      handleAction(
                        "Send Email To Corporate",
                        getSelectedRowIds()
                      )
                    }
                    className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                    Send Email To Corporate
                  </button>
                  <button
                    onClick={() =>
                      handleAction(
                        "Send Email To Providers",
                        getSelectedRowIds()
                      )
                    }
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                    Send Email To Providers
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-white shadow-sm">
        {loading ? (
          <div className="p-4">
            <Skeleton count={10} height={40} className="mb-2" />
          </div>
        ) : data.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="w-full">
                <thead className="bg-gray-50">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                        >
                          <div className="flex items-center">
                            {column.render("Header")}
                            {column.canSort && (
                              <span className="ml-1">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <svg
                                      className="h-5 w-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="h-5 w-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                                      />
                                    </svg>
                                  )
                                ) : (
                                  <svg
                                    className="h-5 w-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M7 7l5-5m0 0l5 5m-5-5v18"
                                    />
                                  </svg>
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="divide-y divide-gray-100"
                >
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={clsx(
                          "hover:bg-gray-50 transition-colors cursor-pointer",
                          selectedRows.has(row.original?.tbl_id) && "bg-blue-50"
                        )}
                        // onClick={() => navigate(`/ape-scheduler/view/${row.original.tbl_id}`)}
                      >
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-4 py-3 text-sm text-gray-800"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Selected Actions Buttons */}
            {selectedRows.size > 0 && selectedStatus && (
              <div className="p-4 bg-blue-50 border-t border-blue-200">
                <div className="flex flex-col sm:flex-row gap-2 justify-between items-center">
                  <span className="text-sm text-blue-800">
                    {selectedRows.size} {selectedStatus} rows selected
                  </span>
                  <div className="flex gap-2">
                    {selectedStatus === "Draft" ? (
                      <button
                        onClick={() =>
                          handleAction(
                            "Finalize & Generate LOA",
                            getSelectedRowIds()
                          )
                        }
                        disabled={isProcessing}
                        className="px-3 py-1 bg-green-600 text-white text-lg rounded hover:bg-green-700 active:scale-95 active:shadow-inner transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="animate-pulse">
                          <FontAwesomeIcon icon={faCheck} className="h-5 w-5" />{" "}
                          Finalize & Generate LOA
                        </span>
                      </button>
                    ) : selectedStatus === "Final" ? (
                      <>
                        <button
                          onClick={() =>
                            handleAction(
                              "Generate Corporate PDF",
                              getSelectedRowIds()
                            )
                          }
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                          Generate Corporate PDF
                        </button>
                        <button
                          onClick={() =>
                            handleAction(
                              "Generate Provider PDF",
                              getSelectedRowIds()
                            )
                          }
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                          Generate Provider PDF
                        </button>
                        <button
                          onClick={() =>
                            handleAction(
                              "Send Email To Corporate",
                              getSelectedRowIds()
                            )
                          }
                          className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          Send Email To Corporate
                        </button>
                        <button
                          onClick={() =>
                            handleAction(
                              "Send Email To Providers",
                              getSelectedRowIds()
                            )
                          }
                          className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          Send Email To Providers
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
              <div className="text-xs text-gray-600">
                Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0}{" "}
                to {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
                {totalCount.toLocaleString()} entries
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={!canPreviousPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canPreviousPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                  First
                </button>
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canPreviousPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Previous
                </button>
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    disabled={isProcessing}
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs",
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                      isProcessing && "cursor-not-allowed opacity-50"
                    )}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canNextPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setCurrentPage(pageCount)}
                  disabled={!canNextPage || isProcessing}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    !canNextPage || isProcessing
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Last
                  <ChevronDoubleRightIcon className="h-5 w-5" />
                </button>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={isProcessing}
                  className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {deleteModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setDeleteModalOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">Confirm Deletion</h2>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">
                  Are you sure you want to delete this schedule? This action
                  cannot be undone.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteModalOpen(false)}
                    disabled={loading}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Deleting…
                      </>
                    ) : (
                      "Yes"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ApeScheduleListPage;
